import {
  personEventSchema,
  type QuestionSeed,
  type SessionEvent,
  type TapeEntry,
} from "@typedoc/interview/events";
import { CAPTURE_GRANT_SECONDS } from "@typedoc/contracts/capture";
import { apply, dueAt, newSession, type Session } from "@typedoc/interview/session";
import { AppError } from "../app-error";
import type { IslandPort } from "./port";

/**
 * A recording in this page's memory, for the build that has no Recordings API behind it and for
 * the browser tests. It keeps a tape exactly as the API does (an entry per input, keys that make
 * a resend harmless, the person's events checked against the contract) and folds it with the
 * session's own `apply`. What the API would add beside the person (the turn detector's breaks,
 * the judgments, capture and the voice worker) the sample plays from a short script, so every
 * state the island draws can be reached here. Everything goes when the page reloads.
 *
 * Scenes start the session in a state worth looking at: `interview` (the default) asks in text
 * as the first release does; `voice` stands in for a voice worker, so spoken words brighten and
 * heard words settle in; `clarifier`, `call`, `offline` and `masked` open on those states;
 * `empty` has nothing to ask, so it ends with nothing left open.
 */
export const SAMPLE_SCENES = [
  "interview",
  "voice",
  "clarifier",
  "call",
  "offline",
  "masked",
  "empty",
] as const;
export type SampleScene = (typeof SAMPLE_SCENES)[number];

/** Runs `run` after `ms`; the answer cancels it. Tests pass one they drive by hand. */
export type Schedule = (run: () => void, ms: number) => () => void;

const browserSchedule: Schedule = (run, ms) => {
  const timer = setTimeout(run, ms);
  return () => clearTimeout(timer);
};

/** Quiet this long, with a question waiting and nothing showing, and the sample calls a break. */
export const SAMPLE_BREAK_MS = 2_500;
/** How long each call takes; the island's pending states are the ones a real call shows. */
const DEFAULT_LATENCY_MS = 120;

const INTENT = "Acknowledge purchase orders";

/** The draft's open questions, carried in from the chat. The last one is asked first. */
export const SAMPLE_QUESTIONS: QuestionSeed[] = [
  {
    id: "q-slot",
    text: "Which pickup slot do you book?",
    urgency: "later",
    origin: "chat",
    evidence: "You mentioned booking the truck in the chat",
    likely: ["Earliest after the pack date", "Whatever's free"],
  },
  {
    id: "q-off",
    text: "What counts as off? Is a few cents on price fine?",
    urgency: "later",
    origin: "screen",
    evidence: "Line 3 differs by $0.04",
    likely: ["Any difference", "Under a dollar is fine"],
  },
  {
    id: "q-match",
    text: "Do you check every line, or only the order total?",
    urgency: "later",
    origin: "screen",
    evidence: "PO 4471 has 14 lines",
    likely: ["Every line", "Only the total", "Depends on the retailer"],
  },
];

/** What the sample's stand-in for capture sees first, and what its judge takes from it. */
const OPENING_ACTION = "Opened the supplier portal, orders filtered to New";
const OPENING_NOTE = {
  topic: "Where it starts",
  text: "Starts in the supplier portal, with orders marked New",
};
/** What the stand-in voice worker hears once the first question is up. */
const HEARD = "Every line, because the retailers change prices without telling us";

/** A word of speech, roughly, for the stand-in voice worker's timing. */
const SPOKEN_WORD_MS = 280;

export type SamplePortOptions = {
  scene?: SampleScene;
  /** The recording the island was opened on; the sample's own when it was opened on none. */
  recordingId?: string;
  /**
   * Where a desktop app would send the sample's screens: this page's `/api`. Nothing there takes
   * them, so a shell that captures a sample drops each one it sends.
   */
  apiBase?: string;
  now?: () => number;
  schedule?: Schedule;
  latencyMs?: number;
};

export type SampleIslandPort = IslandPort & {
  /** The tape so far, for tests. */
  tape: () => readonly TapeEntry[];
  /** Cancels whatever the script still had to do. */
  stop: () => void;
};

export function createSampleIslandPort({
  scene = "interview",
  recordingId = "rec-sample-1",
  apiBase,
  now = Date.now,
  schedule = browserSchedule,
  latencyMs = DEFAULT_LATENCY_MS,
}: SamplePortOptions = {}): SampleIslandPort {
  const tape: TapeEntry[] = [];
  const keys = new Map<string, number>();
  const listeners = new Set<() => void>();
  const pending = new Set<() => void>();
  let session: Session = newSession();
  let counter = 0;
  let grants = 0;
  let cancelBreak: (() => void) | null = null;
  let cancelTimer: (() => void) | null = null;
  let heardOnce = false;
  const spoken = new Set<number>();
  const voice = scene === "voice" || scene === "call" || scene === "offline";

  const wait = (ms: number) =>
    ms <= 0 ? Promise.resolve() : new Promise<void>((resolve) => schedule(resolve, ms));

  /** Runs `run` after `ms`, unless the sample stops first. */
  function later(run: () => void, ms: number) {
    const cancel = schedule(() => {
      pending.delete(cancel);
      run();
    }, ms);
    pending.add(cancel);
    return () => {
      pending.delete(cancel);
      cancel();
    };
  }

  function append(key: string, event: SessionEvent): { seq: number; duplicate: boolean } {
    const known = keys.get(key);
    if (known !== undefined) return { seq: known, duplicate: true };
    const entry: TapeEntry = { seq: tape.length + 1, key, at: now(), event };
    tape.push(entry);
    keys.set(key, entry.seq);
    session = apply(session, entry);
    for (const changed of listeners) changed();
    react(entry);
    wake();
    return { seq: entry.seq, duplicate: false };
  }

  /**
   * The API's timers: a pause held while the person was still working on screen asks once the
   * work has paused too, at the time the session says (`dueAt`). The sample's other limits (the
   * nudge, the stop) are far longer than any sample runs, so only that one is woken.
   */
  function wake() {
    cancelTimer?.();
    cancelTimer = null;
    const due = session.waiting === null ? null : dueAt(session);
    if (due !== null) cancelTimer = later(() => system("timer", { type: "timer" }), due - now());
  }

  /** What the API's other writers add: the turn detector, judgments, capture and the voice worker. */
  const system = (source: string, event: SessionEvent) => {
    counter += 1;
    return append(`${source}:sample-${String(counter)}`, event);
  };

  function react(entry: TapeEntry) {
    const { event } = entry;
    if (event.type === "person.typed" && event.questionId === undefined) judge(entry, event.text);
    if (event.type === "voice.heard") judge(entry, event.text);
    if (voice) speakNewLine();
    // Abacor asks at a break, or at the timer after one held while the screen was still worked.
    const asking = event.type === "break" || event.type === "timer";
    if (voice && asking && session.asked && !heardOnce) {
      heardOnce = true;
      later(() => system("voice", { type: "voice.speaking", speaking: true }), 1_800);
      later(() => {
        system("voice", {
          type: "voice.heard",
          text: HEARD,
          startMs: 0,
          endMs: 3_400,
        });
        system("voice", { type: "voice.speaking", speaking: false });
      }, 3_600);
    }
    quiet(entry);
  }

  /**
   * The sample's judge: a likely answer named in the person's words answers that question;
   * anything else is teaching, and becomes a note. The real judge is a model behind the gateway.
   */
  function judge(input: TapeEntry, text: string) {
    if (session.mode !== "live") return;
    const words = text.toLowerCase();
    for (const q of session.questions) {
      if (q.state === "answered" || q.state === "closed") continue;
      const option = q.likely.find((l) => words.includes(l.toLowerCase()));
      if (option) {
        system("judgment", {
          type: "match.one",
          input: input.seq,
          basedOn: input.seq,
          questionId: q.id,
          took: option,
        });
        return;
      }
    }
    system("judgment", {
      type: "match.none",
      input: input.seq,
      basedOn: input.seq,
      note: { topic: "You said", text: text.slice(0, 280) },
    });
  }

  /** The stand-in voice worker says each new line: started at once, finished when it would be. */
  function speakNewLine() {
    const seq = session.say;
    if (seq === null || spoken.has(seq) || session.voiceMuted) return;
    spoken.add(seq);
    const line = session.transcript.find((l) => l.who === "system" && l.seq === seq);
    if (!line) return;
    const words = line.text.split(" ").length;
    later(
      () =>
        system("voice", {
          type: "say.outcome",
          intent: seq,
          outcome: "started",
        }),
      150,
    );
    later(
      () =>
        system("voice", {
          type: "say.outcome",
          intent: seq,
          outcome: "finished",
        }),
      150 + words * SPOKEN_WORD_MS,
    );
  }

  /** The turn detector: a break once things go quiet with a question waiting and nothing showing. */
  function quiet(entry: TapeEntry) {
    cancelBreak?.();
    cancelBreak = null;
    const s = session;
    // A break that asked nothing would only be followed by another that asks nothing.
    if (entry.event.type === "break") return;
    const waiting = s.questions.some((q) => q.state === "open" || q.state === "passed");
    const free =
      s.mode === "live" &&
      s.asked === null &&
      s.clarifier === null &&
      s.view === null &&
      !s.typing &&
      !s.callOnMic &&
      s.voice !== "offline";
    if (waiting && free)
      cancelBreak = later(() => system("turn", { type: "break" }), SAMPLE_BREAK_MS);
  }

  append("start", {
    type: "session.started",
    intent: INTENT,
    voice: voice ? "live" : "none",
    questions: scene === "empty" ? [] : SAMPLE_QUESTIONS,
  });
  later(() => {
    if (scene === "call") return void system("voice", { type: "mic.taken", taken: true });
    if (scene === "offline")
      return void system("voice", { type: "voice.status", status: "offline" });
    if (scene === "masked") return void system("capture", { type: "screen.masked", masked: true });
    if (scene === "clarifier") {
      const typed = append("person:sample-typed", {
        type: "person.typed",
        text: "Anything over a dollar off, on any line",
      });
      // The judge took this one as fitting two questions, so Abacor asks which.
      cancelBreak?.();
      system("judgment", {
        type: "match.two",
        input: typed.seq,
        basedOn: typed.seq,
        fits: [
          { questionId: "q-match", took: "Checks every line" },
          { questionId: "q-off", took: "Over a dollar off counts" },
        ],
        clarifier: "Is that about which lines you check, or what counts as off?",
      });
      return;
    }
    const action = system("capture", {
      type: "screen.action",
      text: OPENING_ACTION,
    });
    system("judgment", {
      type: "match.none",
      input: action.seq,
      basedOn: action.seq,
      note: OPENING_NOTE,
    });
  }, 400);

  return {
    async read(after) {
      await wait(latencyMs);
      const entries = tape.filter((e) => e.seq > after);
      return { entries: entries.slice(0, 500), more: entries.length > 500 };
    },

    async send(key, event) {
      await wait(latencyMs);
      const parsed = personEventSchema.safeParse(event);
      if (!parsed.success) throw new AppError("VALIDATION_ERROR");
      // The API's own check: an ended recording takes only a look, and Answer now when finished.
      const ended =
        (session.mode === "finished" &&
          event.type !== "person.view" &&
          event.type !== "person.answerNow") ||
        (session.mode === "stopped" && event.type !== "person.view");
      if (ended && !keys.has(`person:${key}`)) throw new AppError("RECORDING_ENDED");
      return append(`person:${key}`, parsed.data);
    },

    watch(changed) {
      listeners.add(changed);
      return () => listeners.delete(changed);
    },

    // As the API does: one recording, a quarter of an hour. Only the sample's own token.
    async grant() {
      await wait(latencyMs);
      grants += 1;
      return {
        recordingId,
        workspaceId: "sample-workspace",
        apiBase: apiBase ?? new URL("/api", globalThis.location.origin).href,
        grant: {
          token: `sample-grant-${String(grants)}`,
          expiresAt: now() + CAPTURE_GRANT_SECONDS * 1000,
        },
      };
    },

    tape: () => tape,

    stop() {
      for (const cancel of pending) cancel();
      pending.clear();
      listeners.clear();
    },
  };
}
